import React from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Trash2Icon } from "lucide-react";
import { parseReflectionMeta } from "@/modules/learningCenter/features/CourseManagement/types/course";
import { CourseFormBlock } from "@/modules/learningCenter/features/CourseManagement/components/CourseForm/types";

interface ReflectionBlockEditorProps {
  block: CourseFormBlock;
  onMetadataChange: (metadata: Record<string, unknown> | null) => void;
}

/**
 * Authoring surface for a `reflection` block: a numbered list of free-text
 * prompts (stored in `metadata.prompts`). The block's `content` stays null.
 */
export function ReflectionBlockEditor({
  block,
  onMetadataChange,
}: ReflectionBlockEditorProps) {
  const { prompts } = parseReflectionMeta(block.metadata);

  const writePrompts = (next: string[]) => {
    onMetadataChange({ ...(block.metadata ?? {}), prompts: next });
  };

  const handlePromptChange = (index: number, value: string) => {
    writePrompts(prompts.map((p, i) => (i === index ? value : p)));
  };

  const handleAddPrompt = () => {
    writePrompts([...prompts, ""]);
  };

  const handleRemovePrompt = (index: number) => {
    writePrompts(prompts.filter((_, i) => i !== index));
  };

  return (
    <div className="space-y-2">
      {prompts.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">
          No prompts yet. Add one below.
        </p>
      ) : (
        prompts.map((prompt, index) => (
          <div className="flex items-start gap-2" key={index}>
            <span className="mt-2 text-sm tabular-nums text-gray-500 dark:text-gray-400">
              {index + 1}.
            </span>
            <Textarea
              aria-label={`Reflection prompt ${index + 1}`}
              onChange={(e) => handlePromptChange(index, e.target.value)}
              placeholder="Ask the learner to reflect…"
              rows={2}
              value={prompt}
            />
            <button
              aria-label={`Remove prompt ${index + 1}`}
              className="mt-2 text-gray-500 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-colors"
              onClick={() => handleRemovePrompt(index)}
              type="button"
            >
              <Trash2Icon className="h-4 w-4" />
            </button>
          </div>
        ))
      )}
      <Button
        onClick={handleAddPrompt}
        size="sm"
        type="button"
        variant="outline"
      >
        Add Prompt
      </Button>
    </div>
  );
}
